import { marked } from "marked";
import DOMPurify from "dompurify";

// Configure marked options
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Custom renderer to add Tailwind classes and handle internal/external links
const renderer = new marked.Renderer();

renderer.link = ({ href, title, text }) => {
  const isInternal = href && href.startsWith("/");
  const titleAttr = title ? ` title="${title}"` : "";
  if (isInternal) {
    return `<a href="${href}"${titleAttr} class="text-blue-600 hover:underline font-medium cursor-pointer" data-internal-link="true">${text}</a>`;
  }
  return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline font-medium">${text}</a>`;
};

// marked v18 token API: Table { header: TableCell[], rows: TableCell[][], align[] };
// TableCell { text, tokens, header, align }. Inline formatting inside cells is
// preserved via parser.parseInline when available.
function renderTableCell(cell: { text: string; tokens?: unknown[] }, align: "center" | "left" | "right" | null, isHeader: boolean): string {
  const tag = isHeader ? "th" : "td";
  const alignClass = align ? ` text-${align}` : " text-left";
  const padding = isHeader ? "px-2 py-1.5 font-semibold text-slate-700" : "px-2 py-1 text-slate-600";
  const parser = (this as unknown as { parser?: { parseInline?: (tokens: unknown[]) => string } }).parser;
  const content = cell.tokens && parser?.parseInline ? parser.parseInline(cell.tokens) : cell.text;
  return `<${tag} class="${padding}${alignClass}">${content ?? cell.text}</${tag}>`;
}

renderer.table = ({ header, rows, align }) => {
  const thead = `<thead class="bg-slate-50 border-b border-slate-200"><tr>${header
    .map((cell, i) => renderTableCell.call(this, cell, align?.[i] ?? null, true))
    .join("")}</tr></thead>`;
  const tbody = `<tbody class="divide-y divide-slate-100">${rows
    .map((row) => `<tr>${row.map((cell, i) => renderTableCell.call(this, cell, align?.[i] ?? null, false)).join("")}</tr>`)
    .join("")}</tbody>`;
  return `<div class="overflow-x-auto my-2 rounded border border-slate-200">
    <table class="w-full border-collapse text-[11px]">
      ${thead}
      ${tbody}
    </table>
  </div>`;
};

renderer.code = ({ text, lang }) => {
  return `<pre class="bg-slate-900 text-slate-100 p-2.5 rounded-lg text-[11px] overflow-x-auto my-2 font-mono"><code class="language-${lang || "text"}">${text}</code></pre>`;
};

renderer.codespan = ({ text }) => {
  return `<code class="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-[11px] font-mono">${text}</code>`;
};

marked.use({ renderer });

export function renderMarkdown(raw: string): string {
  if (!raw) return "";
  try {
    const rawHtml = marked.parse(raw) as string;
    return DOMPurify.sanitize(rawHtml, {
      ADD_ATTR: ["target", "data-internal-link"],
      ALLOWED_TAGS: [
        "h1", "h2", "h3", "h4", "h5", "h6", "p", "a", "ul", "ol", "li",
        "strong", "em", "code", "pre", "table", "thead", "tbody", "tr", "th", "td",
        "blockquote", "hr", "br", "span", "div"
      ],
    });
  } catch {
    return DOMPurify.sanitize(raw);
  }
}
